import React, { useEffect, useMemo, useState } from 'react';
import { useProductos } from '../hooks/useProductos';
import { useCategorias } from '../context/CategoriasContext';
import { obtenerFotos } from '../utils/categorias';
import { valoresDeDimension, coincideDimension, categoriaUsa } from '../utils/agrupar';
import CarruselFotos from './CarruselFotos';
import BotonesCompartirProducto from './BotonesCompartirProducto';
import CarritoPanel from './CarritoPanel';
import PortadaTienda from './PortadaTienda';
import PublicarRedesModal from './PublicarRedesModal';
import CompartirCatalogoModal from './CompartirCatalogoModal';
import { supabase } from '../services/supabaseClient';
import { getNegocioId, simboloMoneda } from '../services/negocioActual';
import { enOferta, precioEfectivo, pctOferta } from '../utils/precios';
import { linkCatalogo, copiarTexto, linkWhatsAppA } from '../utils/compartir';
import { optimizarFoto } from '../utils/imagen';
import { iniciarPixel, iniciarConteo, eventoPixel, datosProducto } from '../utils/metaPixel';
import { capturarOrigen } from '../utils/origenCatalogo';
import {
  leerBolsa, guardarBolsa, agregar, cambiarCantidad, quitar, lineasBolsa, totalBolsa, piezasBolsa,
  leerFavoritos, guardarFavoritos, alternarFavorito, nombreBonito, coincideBusqueda,
} from '../utils/carrito';
import { normalizarDiseno, variablesDiseno } from '../utils/tiendaDiseno';
import { combosVigentes, entraEnCombo } from '../utils/tiendaCombos';
import { useToast } from '../context/ToastContext';
import { actualizarProducto } from '../services/productosService';
import { mensajeAmigable } from '../utils/errores';
import './catalogo.css';

const ORDENES = [['', 'Destacados'], ['nuevo', 'Lo más nuevo'], ['menor', 'Precio: menor a mayor'], ['mayor', 'Precio: mayor a menor']];

// Catálogo público para clientas (se comparte por link, ej. WhatsApp), con cara
// de tienda en línea: portada, buscador, filtros y BOLSA para pedir varias
// prendas juntas. Solo lectura: sin costo, sin stock exacto, sin edición.
// ?cats=RI,CAL limita categorías visibles · ?cat=RI&talla=34B&marca=Leonisa pre-filtra.
export default function CatalogoPublico({ slug = null }) {
  const { productos: productosBase, cargando, error } = useProductos({ publico: true });
  // 🙈 Mostrar/ocultar del catálogo: el cambio se ve al instante, sin recargar.
  const [cambiosCatalogo, setCambiosCatalogo] = useState({});
  const productos = useMemo(() => productosBase.map((p) => (p.id in cambiosCatalogo ? { ...p, activo: cambiosCatalogo[p.id] } : p)), [productosBase, cambiosCatalogo]);
  const { categorias, cargando: cargandoCats } = useCategorias();
  const { mostrarToast } = useToast();
  const negocioId = getNegocioId();
  const params = new URLSearchParams(window.location.search);
  const prefijos = (params.get('cats') || '').split(',').filter(Boolean);
  const [productoUnico, setProductoUnico] = useState(params.get('producto')); // ver/compartir un solo producto
  const catInicial = params.get('cat'); // prefijo de categoría pre-seleccionada

  const [filtro, setFiltro] = useState(null); // nombre de categoría
  const [filtroTipo, setFiltroTipo] = useState(params.get('tipo') || null);
  const [filtroTalla, setFiltroTalla] = useState(params.get('talla') || null);
  const [filtroMarca, setFiltroMarca] = useState(params.get('marca') || null);
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('');
  const [soloFavoritos, setSoloFavoritos] = useState(false);
  const [soloOfertas, setSoloOfertas] = useState(params.get('ofertas') === '1'); // 🏷️ ?ofertas=1 abre solo lo rebajado
  const [negocio, setNegocio] = useState({ nombre: '', telefono: '', instagram: '', logo: '', portada: '', bienvenida: '', color: '', diseno: {}, envio: {}, pago: {}, combos: {} });
  const [esDuena, setEsDuena] = useState(false); // los botones de gestión solo los ve la dueña
  const [puedeOcultar, setPuedeOcultar] = useState(false); // dueña/admin (la vendedora no edita productos)
  const [publicando, setPublicando] = useState(null); // producto a publicar en redes
  const [compartiendo, setCompartiendo] = useState(false); // panel de compartir enlaces
  const [vistaCliente, setVistaCliente] = useState(false); // la dueña previsualiza como clienta
  const [pixelListo, setPixelListo] = useState(false); // píxel de Meta cargado (solo para clientas)
  const [monedaNegocio, setMonedaNegocio] = useState('HNL');
  const [bolsa, setBolsa] = useState(() => leerBolsa(negocioId));
  const [favoritos, setFavoritos] = useState(() => leerFavoritos(negocioId));
  const [bolsaAbierta, setBolsaAbierta] = useState(false);

  useEffect(() => { guardarBolsa(negocioId, bolsa); }, [negocioId, bolsa]);
  useEffect(() => { guardarFavoritos(negocioId, favoritos); }, [negocioId, favoritos]);

  // Recordar de dónde llegó (enlace con ?o=bio, historia, whatsapp…) para el pedido.
  useEffect(() => { capturarOrigen(); }, []);

  useEffect(() => {
    supabase.from('negocios_publicos').select('nombre, telefono, logo_url, color_principal').eq('id', negocioId).maybeSingle()
      .then(({ data }) => setNegocio((n) => ({ ...n, nombre: data?.nombre || 'Catálogo', telefono: data?.telefono || '', logo: data?.logo_url || '', color: data?.color_principal || '' })));
    // Instagram y portada van en consultas aparte y tolerantes: si la columna aún
    // no existe (migración sin aplicar), el catálogo no se rompe.
    supabase.from('negocios_publicos').select('instagram').eq('id', negocioId).maybeSingle()
      .then(({ data }) => setNegocio((n) => ({ ...n, instagram: data?.instagram || '' }))).catch(() => {});
    supabase.from('negocios_publicos').select('portada_url, catalogo_bienvenida').eq('id', negocioId).maybeSingle()
      .then(({ data }) => setNegocio((n) => ({ ...n, portada: data?.portada_url || '', bienvenida: data?.catalogo_bienvenida || '' }))).catch(() => {});
    supabase.from('negocios_publicos').select('tienda_diseno, tienda_envio').eq('id', negocioId).maybeSingle()
      .then(({ data }) => setNegocio((n) => ({ ...n, diseno: data?.tienda_diseno || {}, envio: data?.tienda_envio || {} }))).catch(() => {});
    supabase.from('negocios_publicos').select('tienda_pago').eq('id', negocioId).maybeSingle()
      .then(({ data }) => setNegocio((n) => ({ ...n, pago: data?.tienda_pago || {} }))).catch(() => {});
    supabase.from('negocios_publicos').select('tienda_combos').eq('id', negocioId).maybeSingle()
      .then(({ data }) => setNegocio((n) => ({ ...n, combos: data?.tienda_combos || {} }))).catch(() => {});
  }, [negocioId]);

  // Píxel de Meta del negocio (si lo configuró). No se carga cuando quien mira
  // es alguien del negocio, para no mezclar sus visitas con las de las clientas.
  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        const { data } = await supabase.from('negocios_publicos').select('meta_pixel_id, moneda').eq('id', negocioId).maybeSingle();
        if (!activo || !data) return;
        if (data.moneda) setMonedaNegocio(data.moneda);
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: miembro } = await supabase.from('usuarios_negocios').select('negocio_id')
            .eq('user_id', session.user.id).eq('negocio_id', negocioId).maybeSingle();
          if (miembro) return;
        }
        if (!activo) return;
        // Kontrol cuenta las visitas siempre; el píxel, solo si el negocio lo puso.
        iniciarConteo(negocioId);
        if (data.meta_pixel_id) iniciarPixel(data.meta_pixel_id);
        setPixelListo(true);
      } catch (e) { /* sin píxel, el catálogo funciona igual */ }
    })();
    return () => { activo = false; };
  }, [negocioId]);

  // ¿Quien mira es alguien de este negocio? Entonces ve las herramientas de gestión.
  useEffect(() => {
    let activo = true;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!activo || !session?.user) return;
      const { data } = await supabase.from('usuarios_negocios').select('negocio_id, rol')
        .eq('user_id', session.user.id).eq('negocio_id', negocioId).maybeSingle();
      if (activo && data) { setEsDuena(true); setPuedeOcultar(data.rol !== 'vendedora'); }
    })();
    return () => { activo = false; };
  }, [negocioId]);

  const categoriasVisibles = prefijos.length ? categorias.filter((c) => prefijos.includes(c.prefijo)) : categorias;
  const nombresVisibles = new Set(categoriasVisibles.map((c) => c.nombre));

  // Si el link trae ?cat=<prefijo>, pre-seleccionar esa categoría una vez cargadas.
  useEffect(() => {
    if (catInicial && !filtro && categorias.length) {
      const cat = categorias.find((c) => c.prefijo === catInicial);
      if (cat) setFiltro(cat.nombre);
    }
  }, [catInicial, filtro, categorias]);

  // Si solo hay una categoría visible (ej. link ?cats=RI), seleccionarla
  // para que se muestren los sub-filtros (Tipo, Talla, Marca).
  useEffect(() => {
    if (!filtro && !productoUnico && categoriasVisibles.length === 1) setFiltro(categoriasVisibles[0].nombre);
  }, [filtro, productoUnico, categoriasVisibles]);

  // La clienta solo ve lo disponible (stock ≥ 1) y lo que no está oculto. La dueña
  // ve también agotados y ocultos para gestionarlos, salvo que active "Ver como clienta".
  const modoAdmin = esDuena && !vistaCliente;
  const disponible = (p) => modoAdmin || ((p.stock || 0) >= 1 && p.activo !== false);

  const cambiarCatalogo = async (p, mostrar) => {
    setCambiosCatalogo((c) => ({ ...c, [p.id]: mostrar }));
    try {
      await actualizarProducto(p.id, { activo: mostrar });
      mostrarToast(mostrar ? `🛍️ ${nombreBonito(p.nombre)} ya se ve en tu catálogo` : `🙈 ${nombreBonito(p.nombre)} ya no se ve en tu catálogo`, 'exito', 3000);
    } catch (e) {
      setCambiosCatalogo((c) => ({ ...c, [p.id]: !mostrar }));
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };
  const botonCatalogo = (p) => (modoAdmin && puedeOcultar ? (
    p.activo === false
      ? <button className="cb-admin mostrar" onClick={() => cambiarCatalogo(p, true)}>👁️ Mostrar en el catálogo</button>
      : <button className="cb-admin ocultar" onClick={() => cambiarCatalogo(p, false)}>🙈 Ocultar del catálogo</button>
  ) : null);
  const nOfertas = productos.filter((p) => nombresVisibles.has(p.categoria) && disponible(p) && enOferta(p)).length;
  const prodsBase = productos.filter((p) => nombresVisibles.has(p.categoria) && disponible(p) && (!filtro || p.categoria === filtro) && (!soloOfertas || enOferta(p)));
  const tipos = filtro && categoriaUsa(productos, filtro, 'subtipo') ? valoresDeDimension(prodsBase, 'subtipo').valores : [];
  const marcas = filtro && categoriaUsa(productos, filtro, 'marca') ? valoresDeDimension(prodsBase, 'marca').valores : [];
  const tallas = valoresDeDimension(prodsBase.filter((p) => coincideDimension(p, 'subtipo', filtroTipo)), 'talla').valores;

  const productosVisibles = useMemo(() => {
    const lista = prodsBase
      .filter((p) => coincideDimension(p, 'subtipo', filtroTipo))
      .filter((p) => coincideDimension(p, 'talla', filtroTalla))
      .filter((p) => coincideDimension(p, 'marca', filtroMarca))
      .filter((p) => !soloFavoritos || favoritos.includes(p.id))
      .filter((p) => coincideBusqueda(p, busqueda));
    if (orden === 'menor') return [...lista].sort((a, b) => precioEfectivo(a) - precioEfectivo(b));
    if (orden === 'mayor') return [...lista].sort((a, b) => precioEfectivo(b) - precioEfectivo(a));
    if (orden === 'nuevo') return [...lista].sort((a, b) => String(b.creado_en || '').localeCompare(String(a.creado_en || '')));
    return lista;
  }, [prodsBase, filtroTipo, filtroTalla, filtroMarca, soloFavoritos, favoritos, busqueda, orden]);

  // Bolsa: solo con lo que sigue disponible en el catálogo.
  const { lineas, noDisponibles } = lineasBolsa(bolsa, productos);
  const piezas = piezasBolsa(lineas);
  const enBolsa = new Set(bolsa.map((x) => x.id));
  // Si algo de la bolsa ya se vendió, lo quitamos (una vez cargado el catálogo).
  const [quitadas, setQuitadas] = useState(0);
  useEffect(() => {
    if (cargando || !productos.length || !noDisponibles.length) return;
    setQuitadas(noDisponibles.length);
    setBolsa((b) => b.filter((x) => !noDisponibles.includes(x.id)));
  }, [cargando, productos.length, noDisponibles.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const prodSel = productoUnico ? productos.find((p) => p.id === productoUnico) : null;

  // Meta: la clienta abrió un producto (enlace de anuncio o compartido).
  const prodSelId = prodSel?.id;
  useEffect(() => {
    if (pixelListo && prodSel) eventoPixel('ViewContent', datosProducto(prodSel, precioEfectivo(prodSel), monedaNegocio));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixelListo, prodSelId]);

  const agregarABolsa = (p) => {
    if (enBolsa.has(p.id)) { setBolsaAbierta(true); return; }
    setBolsa((b) => agregar(b, p.id));
    if (pixelListo) eventoPixel('AddToCart', datosProducto(p, precioEfectivo(p), monedaNegocio));
    mostrarToast(`${nombreBonito(p.nombre)} se agregó a tu bolsa 🛍️`, 'exito', 2500);
  };
  const corazon = (p) => setFavoritos((f) => alternarFavorito(f, p.id));

  // "Más de lo mismo" debajo del producto.
  const relacionados = prodSel
    ? productos.filter((p) => p.id !== prodSel.id && p.categoria === prodSel.categoria && disponible(p)
        && (!prodSel.subtipo || coincideDimension(p, 'subtipo', prodSel.subtipo)))
    : [];

  // Abrir otro producto sin recargar la página (el botón "atrás" regresa al anterior).
  const abrirProducto = (id) => {
    const qs = new URLSearchParams(window.location.search);
    if (id) qs.set('producto', id); else qs.delete('producto');
    const q = qs.toString();
    window.history.pushState(null, '', `${window.location.pathname}${q ? `?${q}` : ''}`);
    setProductoUnico(id || null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  useEffect(() => {
    const alVolver = () => setProductoUnico(new URLSearchParams(window.location.search).get('producto'));
    window.addEventListener('popstate', alVolver);
    return () => window.removeEventListener('popstate', alVolver);
  }, []);

  // "Ver todo": quita lo que limitaba el enlace (categoría, tipo, talla…) para
  // que la clienta vea TODA la tienda. Se conserva de dónde llegó (?o=).
  const irATodo = () => {
    const qs = new URLSearchParams(window.location.search);
    ['producto', 'cats', 'cat', 'tipo', 'talla', 'marca', 'ofertas'].forEach((k) => qs.delete(k));
    const q = qs.toString();
    window.history.pushState(null, '', `${window.location.pathname}${q ? `?${q}` : ''}`);
    setProductoUnico(null);
    setFiltro(null); setFiltroTipo(null); setFiltroTalla(null); setFiltroMarca(null); setSoloFavoritos(false); setSoloOfertas(false); setBusqueda('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const elegirCategoria = (nombre) => {
    setFiltro(nombre); setSoloFavoritos(false);
    if (!nombre) setSoloOfertas(false); // "Todo" regresa a toda la tienda
    setFiltroTipo(null); setFiltroTalla(null); setFiltroMarca(null);
  };
  const limpiarFiltros = () => { setBusqueda(''); setFiltroTipo(null); setFiltroTalla(null); setFiltroMarca(null); setSoloFavoritos(false); setSoloOfertas(false); };
  const hayFiltros = busqueda || filtroTipo || filtroTalla || filtroMarca || soloFavoritos || soloOfertas;

  // Si llegó con ?ofertas=1 (historia, WhatsApp), bajar directo a los productos.
  const listoParaOfertas = !cargando && !cargandoCats;
  useEffect(() => {
    if (listoParaOfertas && params.get('ofertas') === '1' && !params.get('producto')) {
      setTimeout(() => document.getElementById('cb-productos')?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, [listoParaOfertas]); // eslint-disable-line react-hooks/exhaustive-deps

  // Comparte la vista TAL CUAL la estás viendo (categoría + talla/tipo/marca + ofertas).
  const compartirVista = async () => {
    const cat = categorias.find((c) => c.nombre === filtro);
    const url = linkCatalogo({ negocioId, slug, prefijos: cat ? [cat.prefijo] : [], talla: filtroTalla, tipo: filtroTipo, marca: filtroMarca, ofertas: soloOfertas });
    const ok = await copiarTexto(url);
    mostrarToast(ok ? 'Link copiado ✅ Pégalo en WhatsApp o Instagram 💕' : url, ok ? 'exito' : 'info', 5000);
  };

  const s = simboloMoneda();
  const combos = useMemo(() => combosVigentes(negocio.combos, new Date(), s), [negocio.combos, s]);
  const comboDe = (p) => combos.find((c) => entraEnCombo(p, c));
  const dinero = (n) => `${s} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const detalleDe = (p) => [p.talla && `Talla ${p.talla}`, p.color].filter(Boolean).join(' · ');
  const precio = (p) => (enOferta(p)
    ? <span className="cb-precio oferta"><s>{dinero(p.precio_venta)}</s>{dinero(precioEfectivo(p))}</span>
    : <span className="cb-precio">{dinero(p.precio_venta)}</span>);

  const escribirTienda = () => {
    const msg = `¡Hola ${negocio.nombre || 'tienda'}! 😊 Estoy viendo su catálogo y tengo una consulta.`;
    window.location.href = linkWhatsAppA(negocio.telefono, msg);
  };

  const tarjeta = (p) => {
    const agotado = (p.stock || 0) < 1;
    const oculto = p.activo === false;
    const foto = obtenerFotos(p)[0];
    const fav = favoritos.includes(p.id);
    return (
      <article className="cb-card" key={p.id}>
        <div style={{ position: 'relative' }}>
          <button className={`cb-card-foto ${agotado ? 'agotado' : ''} ${oculto ? 'oculto' : ''}`} onClick={() => abrirProducto(p.id)} aria-label={`Ver ${nombreBonito(p.nombre)}`}>
            {foto ? <img src={optimizarFoto(foto, 600)} alt={nombreBonito(p.nombre)} loading="lazy" /> : <div className="cb-sinfoto">🛍️</div>}
            {oculto ? <span className="cb-etiqueta oculto">🙈 Oculto</span>
              : agotado ? <span className="cb-etiqueta">{p.apartado ? '⏳ Apartado' : 'Agotado'}</span>
              : enOferta(p) ? <span className="cb-etiqueta oferta">-{pctOferta(p)}%</span> : null}
          </button>
          <button className={`cb-corazon ${fav ? 'si' : ''}`} onClick={() => corazon(p)} aria-label={fav ? 'Quitar de favoritos' : 'Guardar en favoritos'} aria-pressed={fav}>
            {fav ? '♥' : '♡'}
          </button>
        </div>
        <div className="cb-card-info">
          <p className="cb-card-nombre" onClick={() => abrirProducto(p.id)}>{nombreBonito(p.nombre)}</p>
          {detalleDe(p) && <p className="cb-card-detalle">{detalleDe(p)}</p>}
          {!agotado && comboDe(p) && <p className="cb-combo">🎁 Llévate {comboDe(p).nombre}</p>}
          <div className="cb-card-pie">
            {precio(p)}
            {!agotado && (
              <button className={`cb-agregar ${enBolsa.has(p.id) ? 'en-bolsa' : ''}`} onClick={() => agregarABolsa(p)}
                aria-label={enBolsa.has(p.id) ? 'Ya está en tu bolsa' : `Agregar ${nombreBonito(p.nombre)} a mi bolsa`}>
                {enBolsa.has(p.id) ? '✓' : '+ Agregar'}
              </button>
            )}
          </div>
          {modoAdmin && <button className="cb-admin" onClick={() => setPublicando(p)}>📣 Publicar en redes</button>}
          {botonCatalogo(p)}
        </div>
      </article>
    );
  };

  const listo = !cargando && !cargandoCats && !error;
  const titulo = negocio.bienvenida || (negocio.nombre ? `Bienvenida a ${negocio.nombre}` : '');
  const diseno = normalizarDiseno(negocio.diseno);

  return (
    <div className="cb" style={{ ...variablesDiseno(diseno), ...(negocio.color ? { '--cb-acento': negocio.color } : {}) }}>
      {diseno.anuncio && <div className="cb-anuncio">{diseno.anuncio}</div>}
      <header className="cb-barra">
        <button className="cb-marca" onClick={irATodo} aria-label="Ir al inicio del catálogo">
          {negocio.logo && <img src={optimizarFoto(negocio.logo, 120)} alt="" />}
          <span className="cb-serif">{negocio.nombre}</span>
        </button>
        <button className="cb-icono" onClick={() => setBolsaAbierta(true)} aria-label={`Mi bolsa (${piezas})`}>
          👜{piezas > 0 && <span className="cb-insignia">{piezas}</span>}
        </button>
      </header>

      {esDuena && !cargandoCats && (
        <div className="cb-duena">
          <button className="k-btn-ghost" onClick={() => setCompartiendo(true)}>🔗 Compartir catálogo</button>
          <button className="k-btn-ghost" onClick={() => setVistaCliente((v) => !v)}>{vistaCliente ? '↩️ Volver a vista dueña' : '👁️ Ver como clienta'}</button>
          {(filtro || soloOfertas) && <button className="k-btn-ghost" onClick={compartirVista}>🔗 Compartir esta vista{soloOfertas ? ' (ofertas)' : ''}{filtroTalla ? ` (talla ${filtroTalla})` : ''}</button>}
          <small>{vistaCliente ? 'Estás viendo el catálogo como lo ven tus clientas (sin agotados ni ocultos).' : 'Solo tú ves esta barra. La portada y la frase se cambian en Configuración.'}</small>
        </div>
      )}

      {(cargando || cargandoCats) && <p style={{ textAlign: 'center', padding: 60, color: 'var(--cb-suave)' }}>Cargando catálogo…</p>}
      {!cargando && error && <p style={{ textAlign: 'center', padding: 40, color: '#991B1B', fontWeight: 600 }}>⚠️ No se pudo cargar el catálogo. Intenta de nuevo en unos minutos.</p>}

      {/* Un producto (enlace compartido o al tocar una tarjeta) */}
      {listo && productoUnico && (
        <div className="cb-detalle">
          <button className="cb-volver" onClick={irATodo}>← Ver todo el catálogo</button>
          {(!prodSel || !disponible(prodSel)) ? (
            <p style={{ textAlign: 'center', padding: 40, color: 'var(--cb-suave)' }}>Este producto ya no está disponible 💕</p>
          ) : (
            <div className="cb-detalle-grid">
              <div className="cb-detalle-foto">
                <CarruselFotos fotos={obtenerFotos(prodSel)} alt={prodSel.nombre} height={520} ancho={1000} grayscale={(prodSel.stock || 0) < 1} />
              </div>
              <div>
                <h1 className="cb-serif">{nombreBonito(prodSel.nombre)}</h1>
                {prodSel.marca && <p className="cb-sub">{prodSel.marca}</p>}
                {precio(prodSel)}
                {enOferta(prodSel) && <span className="cb-etiqueta oferta" style={{ position: 'static' }}>Oferta -{pctOferta(prodSel)}%</span>}
                {comboDe(prodSel) && <p className="cb-combo" style={{ fontSize: 15 }}>🎁 Combo: llévate {comboDe(prodSel).nombre} (piezas de {dinero(comboDe(prodSel).precioPieza)})</p>}
                {prodSel.descripcion && <p className="cb-desc">{prodSel.descripcion}</p>}
                {(prodSel.talla || prodSel.color || prodSel.subtipo) && (
                  <ul className="cb-ficha">
                    {prodSel.talla && <li><span>Talla</span><b>{prodSel.talla}</b></li>}
                    {prodSel.color && <li><span>Color</span><b>{prodSel.color}</b></li>}
                    {prodSel.subtipo && <li><span>Tipo</span><b>{prodSel.subtipo}</b></li>}
                  </ul>
                )}
                <div className="cb-detalle-acciones">
                  {(prodSel.stock || 0) >= 1 ? (
                    <button className={`cb-btn ${enBolsa.has(prodSel.id) ? 'cb-btn-acento' : 'cb-btn-lleno'}`} onClick={() => agregarABolsa(prodSel)}>
                      {enBolsa.has(prodSel.id) ? '✓ En tu bolsa · Ver bolsa' : 'Agregar a mi bolsa'}
                    </button>
                  ) : <p style={{ margin: 0, color: 'var(--cb-suave)' }}>{prodSel.apartado ? '⏳ Apartado por otra clienta. Escríbenos si te interesa: puede liberarse.' : 'Agotado'}</p>}
                  <p className="cb-letra-chica" style={{ margin: '4px 0 0' }}>¿Tienes preguntas sobre esta pieza? Escríbenos:</p>
                  <BotonesCompartirProducto negocioId={negocioId} producto={prodSel} negocioTelefono={negocio.telefono} negocioNombre={negocio.nombre} negocioInstagram={negocio.instagram} moneda={monedaNegocio} />
                  {modoAdmin && <button className="cb-admin" onClick={() => setPublicando(prodSel)}>📣 Publicar en redes</button>}
                  {botonCatalogo(prodSel)}
                </div>
              </div>
            </div>
          )}
          {prodSel && relacionados.length > 0 && (
            <section className="cb-relacionados">
              <h2 className="cb-serif">También te puede gustar</h2>
              <div className="cb-rejilla">
                {relacionados.slice(0, 8).map((p) => tarjeta(p))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Tienda completa */}
      {listo && !productoUnico && (
        <>
          <PortadaTienda diseno={diseno} nombre={negocio.nombre} logo={negocio.logo} foto={negocio.portada} titulo={titulo}
            onVer={() => document.getElementById('cb-productos')?.scrollIntoView({ behavior: 'smooth' })} />

          <div className="cb-contenedor" id="cb-productos" style={{ scrollMarginTop: 70 }}>
            <div className="cb-buscar">
              <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Busca por nombre, color o talla…" aria-label="Buscar productos" maxLength={60} />
              {busqueda ? <button onClick={() => setBusqueda('')} aria-label="Borrar búsqueda">✕</button> : <span>🔍</span>}
            </div>

            <div className="cb-carril">
              {prefijos.length > 0 && categorias.length > categoriasVisibles.length && (
                <button className="cb-chip" onClick={irATodo}>✨ Ver toda la tienda</button>
              )}
              {categoriasVisibles.length > 1 && (
                <button className={`cb-chip ${!filtro && !soloFavoritos && !soloOfertas ? 'activo' : ''}`} onClick={() => elegirCategoria(null)}>Todo</button>
              )}
              {(nOfertas > 0 || soloOfertas) && (
                <button className={`cb-chip oferta ${soloOfertas ? 'activo' : ''}`} onClick={() => setSoloOfertas((v) => !v)} aria-pressed={soloOfertas}>🏷️ En oferta ({nOfertas})</button>
              )}
              {favoritos.length > 0 && (
                <button className={`cb-chip ${soloFavoritos ? 'activo' : ''}`} onClick={() => setSoloFavoritos((v) => !v)}>♥ Favoritos ({favoritos.length})</button>
              )}
              {categoriasVisibles.length > 1 && categoriasVisibles.map((c) => (
                <button key={c.id} className={`cb-chip ${filtro === c.nombre ? 'activo' : ''}`} onClick={() => elegirCategoria(c.nombre)}>{c.nombre}</button>
              ))}
            </div>

            {tipos.length > 0 && (
              <div className="cb-carril">
                <button className={`cb-chip ${!filtroTipo ? 'activo' : ''}`} onClick={() => { setFiltroTipo(null); setFiltroTalla(null); }}>Todos los tipos</button>
                {tipos.map((t) => <button key={t} className={`cb-chip ${filtroTipo === t ? 'activo' : ''}`} onClick={() => { setFiltroTipo(t); setFiltroTalla(null); }}>{t}</button>)}
              </div>
            )}

            <div className="cb-filtros">
              {tallas.length > 0 && (
                <select className={`cb-select ${filtroTalla ? 'activo' : ''}`} value={filtroTalla || ''} onChange={(e) => setFiltroTalla(e.target.value || null)} aria-label="Filtrar por talla">
                  <option value="">Todas las tallas</option>
                  {tallas.map((v) => <option key={v} value={v}>Talla {v}</option>)}
                </select>
              )}
              {marcas.length > 0 && (
                <select className={`cb-select ${filtroMarca ? 'activo' : ''}`} value={filtroMarca || ''} onChange={(e) => setFiltroMarca(e.target.value || null)} aria-label="Filtrar por marca">
                  <option value="">Todas las marcas</option>
                  {marcas.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              )}
              <select className={`cb-select ${orden ? 'activo' : ''}`} value={orden} onChange={(e) => setOrden(e.target.value)} aria-label="Ordenar">
                {ORDENES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
              {hayFiltros && <button className="cb-limpiar" onClick={limpiarFiltros}>Limpiar filtros</button>}
            </div>

            <p className="cb-conteo">{productosVisibles.length} {productosVisibles.length === 1 ? 'producto' : 'productos'}</p>

            {productosVisibles.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '40px 10px', color: 'var(--cb-suave)' }}>
                {soloOfertas && !nOfertas ? 'Por ahora no hay ofertas. ¡Mira todo lo nuevo de la tienda! 💕'
                  : hayFiltros ? 'No encontramos productos con esos filtros. Prueba con otra talla o búsqueda 💕' : 'Pronto tendremos novedades en esta sección 💕'}
              </p>
            ) : (
              <div className="cb-rejilla">
                {productosVisibles.map((p) => tarjeta(p))}
              </div>
            )}
          </div>
        </>
      )}

      <p className="cb-pie-marca">Tienda creada con Kontrol · Automatika.ia</p>

      {negocio.telefono && (
        <button className="cb-flota-wa" onClick={escribirTienda} aria-label="Escríbenos por WhatsApp" title="Escríbenos por WhatsApp">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.1l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.3-.4.8-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 11.9 11.9 0 0 0 4.6 4c1.7.7 2.4.8 3.2.7.5-.1 1.5-.6 1.8-1.2.2-.6.2-1.1.1-1.2l-.5-.3z" /></svg>
        </button>
      )}

      {piezas > 0 && !bolsaAbierta && (
        <button className={`cb-flota-bolsa ${negocio.telefono ? '' : 'sin-wa'}`} onClick={() => setBolsaAbierta(true)}>
          <span>👜 Ver mi bolsa ({piezas})</span><span>{dinero(totalBolsa(lineas))}</span>
        </button>
      )}

      {bolsaAbierta && (
        <CarritoPanel negocioId={negocioId} negocioNombre={negocio.nombre} negocioTelefono={negocio.telefono} envio={negocio.envio} pago={negocio.pago} combos={combos}
          lineas={lineas} noDisponibles={quitadas}
          onCambiar={(id, n, max) => setBolsa((b) => cambiarCantidad(b, id, n, max))}
          onQuitar={(id) => setBolsa((b) => quitar(b, id))}
          onVaciar={() => setBolsa([])}
          onCerrar={() => { setBolsaAbierta(false); setQuitadas(0); }}
          onPaso={(paso, ls, total) => { if (pixelListo && paso === 'datos') eventoPixel('InitiateCheckout', { value: total, currency: monedaNegocio, num_items: piezasBolsa(ls) }); }}
          onEnviado={(ls, total) => {
            if (pixelListo) eventoPixel('Purchase', { value: total, currency: monedaNegocio, num_items: piezasBolsa(ls), content_ids: ls.map((l) => l.id), content_type: 'product' });
            setBolsa([]);
          }} />
      )}

      {publicando && <PublicarRedesModal producto={publicando} onCerrar={() => setPublicando(null)} />}
      {compartiendo && (
        <CompartirCatalogoModal negocioId={negocioId} slug={slug} categorias={categoriasVisibles} productos={productos} onCerrar={() => setCompartiendo(false)} />
      )}
    </div>
  );
}
